import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import GATracker from "@/components/GATracker";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";

// Route-level code splitting for performance (Phase 5 optimisation).
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Contact = lazy(() => import("@/pages/Contact"));
const Industries = lazy(() => import("@/pages/Industries"));
const IndustryDetail = lazy(() => import("@/pages/IndustryDetail"));
const CaseStudies = lazy(() => import("@/pages/CaseStudies"));
const About = lazy(() => import("@/pages/About"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const CitiesServed = lazy(() => import("@/pages/CitiesServed"));
const FreeWebsiteReview = lazy(() => import("@/pages/FreeWebsiteReview"));
const AuditLanding = lazy(() => import("@/pages/audit/AuditLanding"));
const AuditProcessing = lazy(() => import("@/pages/audit/AuditProcessing"));
const AuditReport = lazy(() => import("@/pages/audit/AuditReport"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminShell = lazy(() => import("@/pages/admin/AdminShell"));
const AdminOverview = lazy(() => import("@/pages/admin/Overview"));
const AdminLeadsCRM = lazy(() => import("@/pages/admin/LeadsCRM"));
const AdminWhatsApp = lazy(() => import("@/pages/admin/WhatsAppAnalytics"));
const AdminLiveChat = lazy(() => import("@/pages/admin/LiveChat"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminHubsCMS = lazy(() => import("@/pages/admin/HubsCMS"));
const AdminHubEditor = lazy(() => import("@/pages/admin/HubEditor"));
const AdminSEO = lazy(() => import("@/pages/admin/SEODashboard"));
const AdminCredibility = lazy(() => import("@/pages/admin/CredibilityAdmin"));
const AdminAudits = lazy(() => import("@/pages/admin/AuditsAdmin"));
const AdminVisitors = lazy(() => import("@/pages/admin/Visitors"));
const AdminMobile = lazy(() => import("@/pages/admin/MobileAdmin"));
const AdminMobileChat = lazy(() => import("@/pages/admin/MobileChat"));
const AdminMobileCalls = lazy(() => import("@/pages/admin/MobileCalls"));
const HubPillar = lazy(() => import("@/pages/hub/HubPillar"));
const HubPage = lazy(() => import("@/pages/hub/HubPage"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const RequestProposal = lazy(() => import("@/pages/RequestProposal"));
const LegalPrivacy = lazy(() => import("@/pages/LegalAndSitemap").then(m => ({ default: m.PrivacyPolicy })));
const LegalTerms = lazy(() => import("@/pages/LegalAndSitemap").then(m => ({ default: m.TermsOfService })));
const LegalSitemap = lazy(() => import("@/pages/LegalAndSitemap").then(m => ({ default: m.HtmlSitemap })));
const LocationDetail = lazy(() => import("@/pages/LocationDetail"));
const KidsLearningAdventure = lazy(() => import("@/pages/KidsLearningAdventure"));
const PrivacyPolicyABCKids = lazy(() => import("@/pages/PrivacyPolicyABCKids"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-white/40 text-sm" aria-live="polite">
    <div className="animate-pulse">Loading…</div>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <GATracker />
        <Toaster theme="dark" position="bottom-right" richColors closeButton />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/services" element={<Layout><Services /></Layout>} />
            <Route path="/services/:slug" element={<Layout><ServiceDetail /></Layout>} />
            {/* Top-level service paths (premium SEO URLs) */}
            <Route path="/website-design" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/ecommerce-development" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/seo-ai-search-optimization" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/ai-automation" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/saas-development" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/mobile-app-development" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/branding" element={<Layout><ServiceDetail /></Layout>} />
            <Route path="/website-maintenance" element={<Layout><ServiceDetail /></Layout>} />

            <Route path="/industries" element={<Layout><Industries /></Layout>} />
            <Route path="/industries/:slug" element={<Layout><IndustryDetail /></Layout>} />
            {/* Premium SEO industry landing URLs */}
            <Route path="/construction-marketing" element={<Layout><IndustryDetail /></Layout>} />
            <Route path="/trades-websites" element={<Layout><IndustryDetail /></Layout>} />
            <Route path="/manufacturing-websites" element={<Layout><IndustryDetail /></Layout>} />
            <Route path="/automotive-marketing" element={<Layout><IndustryDetail /></Layout>} />
            <Route path="/professional-services-websites" element={<Layout><IndustryDetail /></Layout>} />
            <Route path="/startup-development-services" element={<Layout><IndustryDetail /></Layout>} />

            <Route path="/case-studies" element={<Layout><CaseStudies /></Layout>} />
            <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
            <Route path="/about" element={<Layout><About /></Layout>} />
            <Route path="/blog" element={<Layout><Blog /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
            <Route path="/city/:slug" element={<Layout><CityPage /></Layout>} />
            <Route path="/cities-served" element={<Layout><CitiesServed /></Layout>} />
            <Route path="/free-website-review" element={<Layout><FreeWebsiteReview /></Layout>} />
            <Route path="/audit" element={<Layout><AuditLanding /></Layout>} />
            <Route path="/free-website-audit" element={<Layout><AuditLanding /></Layout>} />
            <Route path="/audit/processing/:publicId" element={<Layout><AuditProcessing /></Layout>} />
            <Route path="/audit/report/:publicId" element={<Layout><AuditReport /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
            <Route path="/thank-you" element={<Layout><ThankYou /></Layout>} />
            <Route path="/request-proposal" element={<Layout><RequestProposal /></Layout>} />
            <Route path="/privacy" element={<Layout><LegalPrivacy /></Layout>} />
            <Route path="/terms" element={<Layout><LegalTerms /></Layout>} />
            <Route path="/sitemap" element={<Layout><LegalSitemap /></Layout>} />
            <Route path="/website-design-london" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-manchester" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-birmingham" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-edinburgh" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-new-york" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-dubai" element={<Layout><LocationDetail /></Layout>} />
            {/* Phase 18 — 20 USA city landing pages */}
            <Route path="/website-design-new-york-city-ny" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-los-angeles-ca" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-chicago-il" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-houston-tx" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-phoenix-az" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-philadelphia-pa" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-san-antonio-tx" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-san-diego-ca" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-dallas-tx" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-austin-tx" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-jacksonville-fl" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-san-jose-ca" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-fort-worth-tx" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-columbus-oh" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-charlotte-nc" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-indianapolis-in" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-seattle-wa" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-denver-co" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-washington-dc" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/website-design-boston-ma" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/locations/:slug" element={<Layout><LocationDetail /></Layout>} />
            <Route path="/games" element={<Layout><KidsLearningAdventure /></Layout>} />
            <Route path="/games/kids-learning-adventure" element={<Layout><KidsLearningAdventure /></Layout>} />
            <Route path="/privacy-policy-abc-kids-learning-adventure" element={<Layout><PrivacyPolicyABCKids /></Layout>} />
            <Route path="/admin/login" element={<Layout><AdminLogin /></Layout>} />
            <Route path="/admin/legacy" element={<Layout><AdminDashboard /></Layout>} />
            <Route path="/admin/mobile" element={<AdminMobile />} />
            <Route path="/admin/mobile/chat" element={<AdminMobileChat />} />
            <Route path="/admin/mobile/calls" element={<AdminMobileCalls />} />
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminOverview />} />
              <Route path="leads" element={<AdminLeadsCRM />} />
              <Route path="chat" element={<AdminLiveChat />} />
              <Route path="whatsapp" element={<AdminWhatsApp />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="hubs" element={<AdminHubsCMS />} />
              <Route path="seo" element={<AdminSEO />} />
              <Route path="credibility" element={<AdminCredibility />} />
              <Route path="audits" element={<AdminAudits />} />
              <Route path="visitors" element={<AdminVisitors />} />
              <Route path="hubs/new/edit" element={<AdminHubEditor />} />
              <Route path="hubs/:hubSlug/edit" element={<AdminHubEditor />} />
              <Route path="hubs/:hubSlug/pages/new/edit" element={<AdminHubEditor />} />
              <Route path="hubs/:hubSlug/pages/:pageSlug/edit" element={<AdminHubEditor />} />
            </Route>
            {/* Dynamic content hubs — MUST be last (catch-all). Returns 404 gracefully if slug not in DB. */}
            <Route path="/:hubSlug/:pageSlug" element={<Layout><HubPage /></Layout>} />
            <Route path="/:hubSlug" element={<Layout><HubPillar /></Layout>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
